import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Zap, Target, Flame, Star, Gift } from 'lucide-react';
import { RADIUS } from '../../../config/designTokens';
interface TreasureChestProps {
  fastestTime: number | null;
  accuracy: number;
  longestStreak: number;
  perfectSessions: number;
  dailyGoals: number;
  onOpenCollection?: () => void;
}
export function TreasureChest({
  fastestTime,
  accuracy,
  longestStreak,
  perfectSessions,
  dailyGoals,
  onOpenCollection
}: TreasureChestProps) {
  const getAccuracyLabel = (acc: number) => {
    if (acc >= 90) return 'Meesterlijk';
    if (acc >= 80) return 'Super';
    if (acc >= 70) return 'Goed';
    return 'Oefenend';
  };
  const getSpeedLabel = (ms: number | null) => {
    if (!ms) return 'Nog geen';
    if (ms < 2000) return 'Bliksemsnel';
    if (ms < 4000) return 'Vliegensvlug';
    if (ms < 8000) return 'Vlot';
    return 'Zorgvuldig';
  };
  const treasures = [{
    id: 'accuracy',
    label: 'Nauwkeurigheid',
    value: getAccuracyLabel(accuracy),
    icon: Target,
    color: 'text-blue-500',
    bg: 'bg-blue-100'
  }, {
    id: 'fastest',
    label: 'Snelheid',
    value: getSpeedLabel(fastestTime),
    icon: Zap,
    color: 'text-amber-500',
    bg: 'bg-amber-100'
  }, {
    id: 'streak',
    label: 'Langste Reeks',
    value: `${longestStreak} dagen`,
    icon: Flame,
    color: 'text-orange-500',
    bg: 'bg-orange-100'
  }, {
    id: 'perfect',
    label: 'Foutloze Sessies',
    value: perfectSessions > 0 ? `${perfectSessions}x` : 'Binnenkort',
    icon: Star,
    color: 'text-purple-500',
    bg: 'bg-purple-100'
  }, {
    id: 'goals',
    label: 'Dagdoelen',
    value: dailyGoals > 0 ? `${dailyGoals}x gehaald` : 'Start vandaag',
    icon: Trophy,
    color: 'text-emerald-500',
    bg: 'bg-emerald-100'
  }];
  return <div className="pt-12 pb-24 flex flex-col items-center relative z-10" data-id="element-1959" data-mp="1go0y6jo4smbh">
      <motion.div initial={{
      scale: 0.8,
      opacity: 0
    }} whileInView={{
      scale: 1,
      opacity: 1
    }} viewport={{
      once: true
    }} className="text-center mb-8" data-id="element-1960" data-mp="1f6c0flqwjxdq">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 text-amber-500 rounded-full mb-3 shadow-inner border-4 border-white" data-id="element-1961" data-mp="15aie5andspir">
          <Gift className="w-8 h-8" data-id="element-1962" data-mp="1xxczsavxpuxt" />
        </div>
        <h2 className="text-2xl font-black text-slate-800" data-id="element-1963" data-mp="1xyxrlsqv6zrz">
          Wat je onderweg verzamelde
        </h2>
        <p className="text-slate-500 font-medium mt-1" data-id="element-1964" data-mp="1o7a6saotcgzh">
          Jouw persoonlijke schatkist
        </p>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 w-full max-w-2xl px-4" data-id="element-1965" data-mp="1k681bc4az8p2">
        {treasures.map((treasure, i) => <motion.div key={treasure.id} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.1
      }} className={`bg-white p-4 ${RADIUS.card} border-2 border-slate-100 shadow-sm flex flex-col items-center text-center hover:border-slate-200 transition-colors`} data-id="element-1966" data-mp="2gp5oht3z0tzq" data-mpi={i}>
            <div className={`w-10 h-10 rounded-full ${treasure.bg} ${treasure.color} flex items-center justify-center mb-2`} data-id="element-1967" data-mp="hdcbwbqgo1hb">
              <treasure.icon className="w-5 h-5" data-id="element-1968" data-mp="mamb2j3njric" />
            </div>
            <div className="text-sm sm:text-base font-black text-slate-800 leading-tight mb-0.5" data-id="element-1969" data-mp="r2njftxp8y33">
              {treasure.value}
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-1970" data-mp="v6lh007dm0u1">
              {treasure.label}
            </div>
          </motion.div>)}
      </div>

      {onOpenCollection && <motion.button initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.5
    }} onClick={onOpenCollection} className="mt-8 px-6 py-3 bg-amber-100 text-amber-700 hover:bg-amber-200 font-bold rounded-full transition-colors flex items-center gap-2 shadow-sm" data-id="element-1971" data-mp="rfy7ajzvacb8">
          <Gift className="w-5 h-5" data-id="element-1972" data-mp="3i0p1v02ndp7" />
          Open je Collectie
        </motion.button>}
    </div>;
}